import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  FaSyncAlt,
  FaShareAlt,
  FaCopy,
  FaEye,
  FaUsers,
  FaAward,
  FaBullhorn,
  FaSearch,
  FaFilter,
  FaUndo,
  FaArrowUp,
  FaArrowDown,
  FaNewspaper
} from "react-icons/fa";
import { fetchWithAuth } from "../../utils/api";
import "./ShareAnalytics.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DATE_RANGE_OPTIONS = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "Last 90 Days", value: "90d" },
  { label: "This Year", value: "year" },
  { label: "All Time", value: "all" },
  { label: "Custom Range", value: "custom" }
];

const PLATFORM_COLORS = {
  linkedin: "#0a66c2",
  whatsapp: "#25d366",
  instagram: "#e4405f",
  facebook: "#1877f2",
  x: "#0284c7",
  threads: "#a855f7",
  email: "#ea4335",
  copy_link: "#f59e0b",
  direct: "#64748b",
  other: "#94a3b8"
};

function BlogSharingAnalytics() {
  // Filters State
  const [dateRange, setDateRange] = useState("30d");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedBlog, setSelectedBlog] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [selectedSource, setSelectedSource] = useState("all");
  const [selectedMedium, setSelectedMedium] = useState("all");

  // Dynamic filter options
  const [filterOptions, setFilterOptions] = useState({
    blogs: [],
    platforms: [],
    campaigns: [],
    sources: [],
    mediums: []
  });

  // Data State
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({ totalRows: 0, page: 1, limit: 10, totalPages: 1 });

  // UI State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("landingVisits");
  const [sortOrder, setSortOrder] = useState("desc");
  const [platformChartMetric, setPlatformChartMetric] = useState("all"); // 'all' | 'shares' | 'visits'
  const [blogChartMetric, setBlogChartMetric] = useState("combined"); // 'combined' | 'shares' | 'visits'

  // Calculate Start & End Date based on dateRange
  const getDateBounds = useCallback(() => {
    const now = new Date();
    let start = null;
    let end = now.toISOString().split("T")[0];

    if (dateRange === "7d") {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "30d") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "90d") {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "year") {
      const d = new Date(now.getFullYear(), 0, 1);
      start = d.toISOString().split("T")[0];
    } else if (dateRange === "custom") {
      start = customStartDate || null;
      end = customEndDate || null;
    }

    return { startDate: start, endDate: end };
  }, [dateRange, customStartDate, customEndDate]);

  // Build query string for API calls - strictly Blog scoped
  const buildQueryParams = useCallback(
    (extra = {}) => {
      const { startDate, endDate } = getDateBounds();
      const params = new URLSearchParams();
      params.set("contentType", "blog");

      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (selectedBlog !== "all") params.set("blogId", selectedBlog);
      if (selectedPlatform !== "all") params.set("platform", selectedPlatform);
      if (selectedCampaign !== "all") params.set("campaign", selectedCampaign);
      if (selectedSource !== "all") params.set("source", selectedSource);
      if (selectedMedium !== "all") params.set("medium", selectedMedium);

      Object.entries(extra).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          params.set(k, v);
        }
      });

      return params.toString();
    },
    [getDateBounds, selectedBlog, selectedPlatform, selectedCampaign, selectedSource, selectedMedium]
  );

  // Fetch Filter Dropdown Options for Blogs
  const fetchFilterOptions = async () => {
    try {
      const res = await fetchWithAuth("/admin/blog-share-analytics/filter-options");
      if (res.ok) {
        const json = await res.json();
        if (json.success && json.options) {
          setFilterOptions({
            blogs: json.options.blogs || json.options.items || [],
            platforms: json.options.platforms || [],
            campaigns: json.options.campaigns || [],
            sources: json.options.sources || [],
            mediums: json.options.mediums || []
          });
        }
      }
    } catch (err) {
      console.warn("Could not fetch blog filter options:", err);
    }
  };

  // Fetch Dashboard Stats, Charts & Table
  const fetchDashboardData = useCallback(async () => {
    setError(null);
    try {
      const q = buildQueryParams();

      // 1. Stats
      const statsRes = await fetchWithAuth(`/admin/blog-share-analytics/stats?${q}`);
      const statsData = await statsRes.json();

      // 2. Charts
      const chartsRes = await fetchWithAuth(`/admin/blog-share-analytics/charts?${q}`);
      const chartsData = await chartsRes.json();

      // 3. Table
      const tableQ = buildQueryParams({
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery,
        sortBy,
        sortOrder
      });
      const tableRes = await fetchWithAuth(`/admin/blog-share-analytics/table?${tableQ}`);
      const tableJson = await tableRes.json();

      if (statsData.success) setStats(statsData.stats);
      if (chartsData.success) setCharts(chartsData.charts);
      if (tableJson.success) {
        setTableData(tableJson.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRows: tableJson.pagination?.totalRows || 0,
          totalPages: tableJson.pagination?.totalPages || 1
        }));
      }
    } catch (err) {
      console.error("Error fetching blog share analytics:", err);
      setError("Failed to load blog share analytics. Please verify your connection or login session.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildQueryParams, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  // Initial load
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Handle Refresh Click
  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
    fetchFilterOptions();
  };

  // Reset all filters to default
  const handleResetFilters = () => {
    setDateRange("30d");
    setCustomStartDate("");
    setCustomEndDate("");
    setSelectedBlog("all");
    setSelectedPlatform("all");
    setSelectedCampaign("all");
    setSelectedSource("all");
    setSelectedMedium("all");
    setSearchQuery("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Toggle Table Sort
  const handleSortChange = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortOrder("desc");
    }
  };

  // Safe delta renderer
  const renderTrendBadge = (change) => {
    if (change === null || change === undefined) {
      return <span className="trend-pill trend-neutral">Period baseline</span>;
    }
    if (change > 0) {
      return (
        <span className="trend-pill trend-up">
          <FaArrowUp /> +{change}%
        </span>
      );
    }
    if (change < 0) {
      return (
        <span className="trend-pill trend-down">
          <FaArrowDown /> {change}%
        </span>
      );
    }
    return <span className="trend-pill trend-neutral">0% vs prev</span>;
  };

  // Chart 1: Platform Distribution (Doughnut)
  const getPlatformDoughnutData = () => {
    if (!charts?.platformDistribution) return null;

    const dist = charts.platformDistribution;
    const labels = ["LinkedIn", "WhatsApp", "Instagram", "Facebook", "X", "Threads", "Email", "Copy Link", "Direct"];
    const keys = ["linkedin", "whatsapp", "instagram", "facebook", "x", "threads", "email", "copy_link", "direct"];

    let counts = [];
    if (platformChartMetric === "shares") {
      counts = keys.map((k) => dist.shares?.[k] || 0);
    } else if (platformChartMetric === "visits") {
      counts = keys.map((k) => dist.visits?.[k] || 0);
    } else {
      counts = keys.map((k) => (dist.shares?.[k] || 0) + (dist.visits?.[k] || 0));
    }

    const bgColors = keys.map((k) => PLATFORM_COLORS[k] || "#94a3b8");

    return {
      labels,
      datasets: [
        {
          data: counts,
          backgroundColor: bgColors,
          borderWidth: 2,
          borderColor: "#0f172a",
          hoverOffset: 6
        }
      ]
    };
  };

  // Chart 2: Blog Performance (Bar)
  const getBlogBarData = () => {
    const dataList = (charts?.blogPerformance || charts?.performanceList || []).slice(0, 8);
    if (!dataList.length) return null;

    const labels = dataList.map((p) => (p.title.length > 20 ? p.title.substring(0, 18) + "..." : p.title));

    if (blogChartMetric === "shares") {
      return {
        labels,
        datasets: [
          {
            label: "Share Actions",
            data: dataList.map((p) => p.shares),
            backgroundColor: "rgba(56, 189, 248, 0.8)",
            borderRadius: 6
          }
        ]
      };
    } else if (blogChartMetric === "visits") {
      return {
        labels,
        datasets: [
          {
            label: "Tracked Landing Visits",
            data: dataList.map((p) => p.visits),
            backgroundColor: "rgba(34, 197, 94, 0.8)",
            borderRadius: 6
          }
        ]
      };
    }

    return {
      labels,
      datasets: [
        {
          label: "Tracked Landing Visits",
          data: dataList.map((p) => p.visits),
          backgroundColor: "rgba(34, 197, 94, 0.8)",
          borderRadius: 6
        },
        {
          label: "Share Actions Initiated",
          data: dataList.map((p) => p.shares),
          backgroundColor: "rgba(56, 189, 248, 0.8)",
          borderRadius: 6
        }
      ]
    };
  };

  // Chart 3: Traffic Trends (Line)
  const getTrafficTrendsData = () => {
    if (!charts?.trafficTrends?.length) return null;

    const trends = charts.trafficTrends;
    const labels = trends.map((t) => t.date);

    return {
      labels,
      datasets: [
        {
          label: "Tracked Article Visits",
          data: trends.map((t) => t.visits),
          borderColor: "#22c55e",
          backgroundColor: "rgba(34, 197, 94, 0.15)",
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: "Blog Share Actions",
          data: trends.map((t) => t.shares),
          borderColor: "#38bdf8",
          backgroundColor: "rgba(56, 189, 248, 0.15)",
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  };

  // Chart 4: Campaign Performance (Bar)
  const getCampaignBarData = () => {
    if (!charts?.campaignPerformance?.length) return null;

    const list = charts.campaignPerformance.slice(0, 8);
    return {
      labels: list.map((c) => (c.campaign.length > 18 ? c.campaign.substring(0, 16) + "..." : c.campaign)),
      datasets: [
        {
          label: "Tracked Reader Visits",
          data: list.map((c) => c.visits),
          backgroundColor: "rgba(168, 85, 247, 0.8)",
          borderRadius: 6
        },
        {
          label: "Share Actions",
          data: list.map((c) => c.shares),
          backgroundColor: "rgba(245, 158, 11, 0.8)",
          borderRadius: 6
        }
      ]
    };
  };

  // Chart 5: Source & Medium Breakdown (Horizontal / Stacked Bar)
  const getSourceMediumData = () => {
    if (!charts?.sourceMediumAnalysis?.length) return null;

    const list = charts.sourceMediumAnalysis.slice(0, 8);
    return {
      labels: list.map((sm) => `${sm.source} / ${sm.medium}`),
      datasets: [
        {
          label: "Recorded Reader Touchpoints",
          data: list.map((sm) => sm.count),
          backgroundColor: "rgba(99, 102, 241, 0.8)",
          borderRadius: 6
        }
      ]
    };
  };

  // Common chart styling options for dark UI
  const chartDefaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: "#cbd5e1",
          font: { family: "'Inter', sans-serif", size: 12 }
        }
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        titleColor: "#f8fafc",
        bodyColor: "#cbd5e1",
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
        padding: 10
      }
    },
    scales: {
      x: {
        grid: { color: "rgba(255, 255, 255, 0.05)" },
        ticks: { color: "#94a3b8", font: { size: 11 } }
      },
      y: {
        grid: { color: "rgba(255, 255, 255, 0.05)" },
        ticks: { color: "#94a3b8", font: { size: 11 }, precision: 0 },
        beginAtZero: true
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          color: "#cbd5e1",
          font: { family: "'Inter', sans-serif", size: 11 },
          padding: 12
        }
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        titleColor: "#f8fafc",
        bodyColor: "#cbd5e1",
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
        padding: 10
      }
    }
  };

  return (
    <div className="share-analytics-container">
      {/* HEADER SECTION */}
      <div className="analytics-header-card">
        <div className="header-text-block">
          <div className="header-badge-row">
            <span className="live-pulse-dot" style={{ backgroundColor: "#a855f7", boxShadow: "0 0 10px #a855f7" }}></span>
            <span className="badge-label" style={{ color: "#c084fc" }}>Blog Readership Intelligence</span>
          </div>
          <h1>📰 Blog Sharing Analytics & UTM Tracking</h1>
          <p>
            Monitor blog readership campaigns, article social shares, reader acquisition channels, and viral reach.
          </p>
        </div>

        <div className="header-controls-row">
          <button
            className={`refresh-btn ${refreshing ? "spinning" : ""}`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh blog analytics data"
          >
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* FILTER CONTROLS BAR */}
      <div className="analytics-filter-bar">
        <div className="filter-group">
          <label>
            <FaFilter /> Date Range:
          </label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {dateRange === "custom" && (
          <div className="filter-group custom-date-inputs">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              title="Start Date"
            />
            <span>to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              title="End Date"
            />
          </div>
        )}

        <div className="filter-group">
          <label><FaNewspaper style={{ marginRight: "4px" }} /> Blog Article:</label>
          <select value={selectedBlog} onChange={(e) => setSelectedBlog(e.target.value)}>
            <option value="all">All Blog Articles</option>
            {filterOptions.blogs.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Platform:</label>
          <select value={selectedPlatform} onChange={(e) => setSelectedPlatform(e.target.value)}>
            <option value="all">All Platforms</option>
            {filterOptions.platforms.map((plat) => (
              <option key={plat} value={plat}>
                {plat.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Campaign:</label>
          <select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)}>
            <option value="all">All Campaigns</option>
            {filterOptions.campaigns.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <button className="reset-filter-btn" onClick={handleResetFilters} title="Reset all filters">
          <FaUndo /> Reset
        </button>
      </div>

      {/* ERROR NOTICE */}
      {error && (
        <div className="analytics-error-banner">
          <p>⚠️ {error}</p>
          <button onClick={fetchDashboardData}>Try Again</button>
        </div>
      )}

      {/* LOADING STATE */}
      {loading ? (
        <div className="analytics-loading-box">
          <div className="spinner"></div>
          <p>Crunching blog share events & readership traffic metrics...</p>
        </div>
      ) : (
        <>
          {/* KPI SUMMARY CARDS (7 METRICS) */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-blue">
                  <FaShareAlt />
                </span>
                {renderTrendBadge(stats?.comparison?.sharesChange)}
              </div>
              <p className="kpi-label">Total Blog Share Actions</p>
              <h3 className="kpi-value">{stats?.totalShares?.toLocaleString() || 0}</h3>
              <p className="kpi-sub">Article shares initiated</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-amber">
                  <FaCopy />
                </span>
                {renderTrendBadge(stats?.comparison?.copiesChange)}
              </div>
              <p className="kpi-label">Total Blog Copy Actions</p>
              <h3 className="kpi-value">{stats?.totalCopies?.toLocaleString() || 0}</h3>
              <p className="kpi-sub">Direct article link copies</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-green">
                  <FaEye />
                </span>
                {renderTrendBadge(stats?.comparison?.visitsChange)}
              </div>
              <p className="kpi-label">Tracked Blog Visits</p>
              <h3 className="kpi-value">{stats?.totalVisits?.toLocaleString() || 0}</h3>
              <p className="kpi-sub">Visits via tracked UTM links</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-purple">
                  <FaUsers />
                </span>
                <span className="trend-pill trend-neutral">Privacy-safe</span>
              </div>
              <p className="kpi-label">Unique Blog Readers</p>
              <h3 className="kpi-value">{stats?.uniqueVisitors?.toLocaleString() || 0}</h3>
              <p className="kpi-sub">Deduplicated readership</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-pink">
                  <FaAward />
                </span>
                <span className="trend-pill trend-neutral">{stats?.topPlatformCount || 0} hits</span>
              </div>
              <p className="kpi-label">Top Sharing Platform for Blogs</p>
              <h3 className="kpi-value kpi-text-highlight">
                {stats?.topPlatform ? stats.topPlatform.toUpperCase() : "N/A"}
              </h3>
              <p className="kpi-sub">Most active reader network</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-cyan">
                  <FaNewspaper />
                </span>
                <span className="trend-pill trend-neutral">{stats?.topBlogCount || stats?.topProjectCount || 0} events</span>
              </div>
              <p className="kpi-label">Top Shared Blog</p>
              <h3 className="kpi-value kpi-text-highlight" title={stats?.topBlog || stats?.topProject}>
                {stats?.topBlog || stats?.topProject || "N/A"}
              </h3>
              <p className="kpi-sub">Most viral article</p>
            </div>

            <div className="kpi-card">
              <div className="kpi-card-header">
                <span className="kpi-icon-wrap icon-indigo">
                  <FaBullhorn />
                </span>
                <span className="trend-pill trend-neutral">In range</span>
              </div>
              <p className="kpi-label">Active Blog Campaigns</p>
              <h3 className="kpi-value">{stats?.activeCampaigns || 0}</h3>
              <p className="kpi-sub">Distinct active UTM campaigns</p>
            </div>
          </div>

          {/* CHARTS GRID 1: Traffic Trends & Platform Doughnut */}
          <div className="charts-main-grid">
            {/* Traffic Timeline */}
            <div className="chart-card chart-large">
              <div className="chart-header">
                <div>
                  <h3>📈 Blog Readership Trends Over Time</h3>
                  <p className="chart-sub">Tracked article visits & shares across the selected timeframe</p>
                </div>
              </div>
              <div className="chart-canvas-wrapper" style={{ height: "300px" }}>
                {getTrafficTrendsData() ? (
                  <Line data={getTrafficTrendsData()} options={chartDefaultOptions} />
                ) : (
                  <div className="empty-chart-state">
                    <p>No blog traffic trend data recorded yet in this time window.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Platform Distribution */}
            <div className="chart-card chart-small">
              <div className="chart-header">
                <div>
                  <h3>🍩 Blog Platform Distribution</h3>
                  <p className="chart-sub">Reader traffic by social & communication channels</p>
                </div>
                <div className="chart-pill-selector">
                  <button
                    className={platformChartMetric === "all" ? "active" : ""}
                    onClick={() => setPlatformChartMetric("all")}
                  >
                    All
                  </button>
                  <button
                    className={platformChartMetric === "visits" ? "active" : ""}
                    onClick={() => setPlatformChartMetric("visits")}
                  >
                    Visits
                  </button>
                  <button
                    className={platformChartMetric === "shares" ? "active" : ""}
                    onClick={() => setPlatformChartMetric("shares")}
                  >
                    Shares
                  </button>
                </div>
              </div>
              <div className="chart-canvas-wrapper" style={{ height: "300px" }}>
                {getPlatformDoughnutData() ? (
                  <Doughnut data={getPlatformDoughnutData()} options={doughnutOptions} />
                ) : (
                  <div className="empty-chart-state">
                    <p>No blog platform activity recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* CHARTS GRID 2: Blog Performance & Campaign Comparison */}
          <div className="charts-main-grid">
            {/* Blog Performance Bar */}
            <div className="chart-card chart-large">
              <div className="chart-header">
                <div>
                  <h3>📊 Blog Article Performance Comparison</h3>
                  <p className="chart-sub">Shares vs visits across blog articles</p>
                </div>
                <div className="chart-pill-selector">
                  <button
                    className={blogChartMetric === "combined" ? "active" : ""}
                    onClick={() => setBlogChartMetric("combined")}
                  >
                    Combined
                  </button>
                  <button
                    className={blogChartMetric === "visits" ? "active" : ""}
                    onClick={() => setBlogChartMetric("visits")}
                  >
                    Visits
                  </button>
                  <button
                    className={blogChartMetric === "shares" ? "active" : ""}
                    onClick={() => setBlogChartMetric("shares")}
                  >
                    Shares
                  </button>
                </div>
              </div>
              <div className="chart-canvas-wrapper" style={{ height: "280px" }}>
                {getBlogBarData() ? (
                  <Bar data={getBlogBarData()} options={chartDefaultOptions} />
                ) : (
                  <div className="empty-chart-state">
                    <p>No blog share data available for this selection.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Campaign Performance Bar */}
            <div className="chart-card chart-small">
              <div className="chart-header">
                <div>
                  <h3>🎯 Blog UTM Campaign Performance</h3>
                  <p className="chart-sub">Top readership campaigns by acquisition</p>
                </div>
              </div>
              <div className="chart-canvas-wrapper" style={{ height: "280px" }}>
                {getCampaignBarData() ? (
                  <Bar data={getCampaignBarData()} options={chartDefaultOptions} />
                ) : (
                  <div className="empty-chart-state">
                    <p>No blog campaign data recorded yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* CHARTS GRID 3: Source / Medium Breakdown */}
          {charts?.sourceMediumAnalysis?.length > 0 && (
            <div className="chart-card full-width">
              <div className="chart-header">
                <div>
                  <h3>🌐 Source & Medium Channel Breakdown</h3>
                  <p className="chart-sub">Contribution of UTM source and UTM medium dimensions for blogs</p>
                </div>
              </div>
              <div className="chart-canvas-wrapper" style={{ height: "240px" }}>
                <Bar data={getSourceMediumData()} options={chartDefaultOptions} />
              </div>
            </div>
          )}

          {/* ANALYTICS DATA TABLE */}
          <div className="analytics-table-card">
            <div className="table-card-header">
              <div>
                <h3>📋 Blog Sharing & Reader Traffic Data Table</h3>
                <p className="table-sub">Detailed breakdown of recorded shares, landing visits, and UTM parameters for blog articles</p>
              </div>

              <div className="table-search-box">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search blog article, campaign, source..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                />
              </div>
            </div>

            <div className="table-responsive-wrapper">
              <table className="analytics-data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSortChange("blogTitle")}>
                      Blog Article {sortBy === "blogTitle" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th onClick={() => handleSortChange("platform")}>
                      Platform {sortBy === "platform" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th>UTM Source</th>
                    <th>UTM Medium</th>
                    <th onClick={() => handleSortChange("campaign")}>
                      Campaign {sortBy === "campaign" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th onClick={() => handleSortChange("shareActions")}>
                      Shares {sortBy === "shareActions" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th onClick={() => handleSortChange("copyActions")}>
                      Copies {sortBy === "copyActions" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th onClick={() => handleSortChange("landingVisits")}>
                      Visits {sortBy === "landingVisits" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                    <th onClick={() => handleSortChange("lastSeen")}>
                      Last Active {sortBy === "lastSeen" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="table-empty-td">
                        No blog analytics records match the selected filter criteria. Data will appear once blog articles are shared or visited via tracked links!
                      </td>
                    </tr>
                  ) : (
                    tableData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="td-project-title">
                          <strong>{row.blogTitle || row.title || row.projectTitle || row.blogId}</strong>
                        </td>
                        <td>
                          <span
                            className="platform-table-badge"
                            style={{
                              backgroundColor: `${PLATFORM_COLORS[row.platform?.toLowerCase()] || "#64748b"}22`,
                              color: PLATFORM_COLORS[row.platform?.toLowerCase()] || "#94a3b8"
                            }}
                          >
                            {row.platform ? row.platform.toUpperCase() : "DIRECT"}
                          </span>
                        </td>
                        <td>{row.source || <span className="text-muted">—</span>}</td>
                        <td>{row.medium || <span className="text-muted">—</span>}</td>
                        <td>
                          <code className="campaign-code">{row.campaign || "none"}</code>
                        </td>
                        <td>
                          <span className="badge-metric shares">{row.shareActions || 0}</span>
                        </td>
                        <td>
                          <span className="badge-metric copies">{row.copyActions || 0}</span>
                        </td>
                        <td>
                          <span className="badge-metric visits">{row.landingVisits || 0}</span>
                        </td>
                        <td className="text-muted">
                          {row.lastSeen ? new Date(row.lastSeen).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="table-pagination-footer">
                <span className="pagination-info">
                  Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalRows} records)
                </span>
                <div className="pagination-buttons">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <span className="page-current">{pagination.page}</span>
                  <button
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default BlogSharingAnalytics;
